/**
 * @fileoverview View toggle component for switching between list and graph views
 */

import React from 'react';
import {
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

// Mismo aspecto para el botón de crear y los de vista: se leen como una
// sola barra de herramientas.
const toolButtonSx = {
  border: '1px solid var(--wb-x-border-ffffff33, rgba(255, 255, 255, 0.2))',
  color: 'var(--wb-text-soft)',
  '&.Mui-selected': {
    backgroundColor: 'var(--wb-x-bg-6366f133, rgba(99, 102, 241, 0.2))',
    color: 'var(--wb-indigo)',
    '&:hover': {
      backgroundColor: 'var(--wb-x-bg-6366f14c, rgba(99, 102, 241, 0.3))',
    },
  },
  '&:hover': {
    backgroundColor: 'var(--wb-x-bg-ffffff0d, rgba(255, 255, 255, 0.05))',
  },
};

/**
 * ViewToggle component for switching between list and graph views
 * @param {Object} props - Component props
 * @param {'list' | 'graph'} props.currentView - Current active view
 * @param {Function} props.onViewChange - Callback when view changes
 * @param {boolean} [props.disabled] - Whether the toggle is disabled
 * @param {string} [props.size] - Size of the toggle buttons
 * @param {Function} [props.onCreate] - Si se pasa, muestra a la izquierda el
 *   botón "Nuevo modelo" con el mismo estilo que los de vista
 * @returns {React.Component} ViewToggle component
 */
const ViewToggle = ({ 
  currentView, 
  onViewChange, 
  disabled = false,
  size = 'medium',
  onCreate
}) => {
  const handleViewChange = (event, newView) => {
    if (newView !== null && newView !== currentView) {
      onViewChange(newView);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {onCreate && (
        <Tooltip title="Nuevo modelo">
          <ToggleButton
            value="create"
            selected={false}
            onChange={onCreate}
            aria-label="Nuevo modelo"
            size={size}
            disabled={disabled}
            sx={toolButtonSx}
          >
            <AddIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
      )}
      <ToggleButtonGroup
        value={currentView}
        exclusive
        onChange={handleViewChange}
        aria-label="view toggle"
        size={size}
        disabled={disabled}
        sx={{ '& .MuiToggleButton-root': toolButtonSx }}
      >
        <ToggleButton value="list" aria-label="list view">
          <Tooltip title="Vista de lista">
            <ViewListIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="graph" aria-label="graph view">
          <Tooltip title="Vista de grafo">
            <AccountTreeIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default ViewToggle;